// structureSnapshot.js — H1.3 (frontend structure SSOT)
//
// Single cached client for GET /api/users/registration-structure. The
// OrganizationNode tree the Super Admin manages is the source of truth for
// every dropdown that used to read a static map (branchHierarchy.js etc.).
//
// - fetchStructureSnapshot(): module-level cached fetch (one request per app
//   session; force=true revalidates).
// - useStructureSnapshot(legacyFallback): React hook. Returns the DB tree when
//   available, otherwise the provided legacy static fallback (DB-down never
//   blanks the UI; behaviour matches the DB-first pattern of the routing
//   service). Merges DB entries on top of the legacy keys so a fresh node
//   added in the Structure Manager appears immediately.
import { useEffect, useState } from "react";
import { getApiBaseUrl } from "./baseUrl";

let cachedNodes = null;
let inFlight = null;

export const fetchStructureSnapshot = async (force = false) => {
  if (!force && cachedNodes) return cachedNodes;
  if (!force && inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/users/registration-structure`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      if (data?.nodes && typeof data.nodes === "object") {
        cachedNodes = data.nodes;
        return cachedNodes;
      }
      throw new Error("Malformed registration-structure response.");
    } finally {
      inFlight = null;
    }
  })();

  try {
    return await inFlight;
  } catch {
    // Fail soft: caller decides the fallback. Cache stays untouched.
    return null;
  }
};

export const clearStructureSnapshotCache = () => {
  cachedNodes = null;
};

// Merges the DB tree over a legacy static tree (same shape as hierarchy.js).
// DB entries win; legacy-only keys survive as the offline fallback.
export const mergeStructure = (legacy, dynamic) => {
  if (!dynamic || typeof dynamic !== "object") return legacy || {};
  if (!legacy || typeof legacy !== "object") return dynamic;
  const merged = { ...legacy };
  Object.entries(dynamic).forEach(([key, payload]) => {
    if (!merged[key]) {
      merged[key] = payload;
      return;
    }
    merged[key] = {
      ...merged[key],
      ...(payload?.subBranches
        ? {
          subBranches: {
            ...(merged[key]?.subBranches || {}),
            ...payload.subBranches,
          },
        }
        : {}),
    };
  });
  return merged;
};

// Index of "branch name -> sub-branch names" across the whole tree.
// Reproduces the semantics the deleted branchHierarchy.js used to provide
// (including the HQ KA branches nested under subBranches["HQ KA"]), but from
// the DB snapshot instead of a static file.
export const buildSubBranchIndex = (tree) => {
  const index = {};
  const addEntry = (branchName, names) => {
    if (!branchName) return;
    const existing = index[branchName] || [];
    names.forEach((name) => {
      if (name && !existing.includes(name)) existing.push(name);
    });
    index[branchName] = existing;
  };

  const walk = (node) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return;
    Object.entries(node).forEach(([key, value]) => {
      if (Array.isArray(value) || !value || typeof value !== "object") return;
      if (value.subBranches) {
        // Formation/unit level: key maps to its child branch/unit names.
        addEntry(key, Object.keys(value.subBranches));
        walk(value.subBranches);
        return;
      }
      // Branch/section level: key maps to its sub-branch/section names.
      addEntry(key, Object.keys(value));
      walk(value);
    });
  };

  walk(tree);
  return index;
};

let cachedServiceTrees = null;
export const fetchServiceTrees = async (force = false) => {
  if (!force && cachedServiceTrees !== null) return cachedServiceTrees;
  const data = await fetchRawRegistrationStructure(force);
  if (data && typeof data.serviceTrees === "object" && data.serviceTrees !== null) {
    cachedServiceTrees = data.serviceTrees;
    return cachedServiceTrees;
  }
  return null; // backend without serviceTrees — callers fall back
};

export const useServiceTreesSnapshot = () => {
  const [trees, setTrees] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchServiceTrees().then((result) => {
      if (!cancelled && result) setTrees(result);
    });
    return () => { cancelled = true; };
  }, []);
  return trees;
};

// Raw fetch of the full registration-structure payload (nodes + flat +
// serviceTrees). Shared by fetchStructureSnapshot and fetchServiceTrees so a
// single request serves both consumers.
let rawPayloadPromise = null;
const fetchRawRegistrationStructure = async (force = false) => {
  if (!force && rawPayloadPromise) return rawPayloadPromise;
  rawPayloadPromise = (async () => {
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/users/registration-structure`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json().catch(() => null);
    } catch {
      return null;
    } finally {
      rawPayloadPromise = null;
    }
  })();
  return rawPayloadPromise;
};

export const useStructureSnapshot = (legacyFallback = {}) => {
  const [structure, setStructure] = useState(legacyFallback);

  useEffect(() => {
    let cancelled = false;
    fetchStructureSnapshot().then((nodes) => {
      if (!cancelled && nodes) {
        setStructure((prev) => mergeStructure(prev, nodes));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return structure;
};

export default {
  fetchStructureSnapshot,
  clearStructureSnapshotCache,
  mergeStructure,
  buildSubBranchIndex,
  useStructureSnapshot,
  fetchServiceTrees,
  useServiceTreesSnapshot,
};
